import React, { useEffect, useRef, useState } from 'react';
import { TextField } from '@mui/material';

/**
 * A text field that edits a value in place and commits on blur (or Enter for single-line).
 *
 * The builder is inline now — there is no modal with a Save button — so each editable label owns
 * its own draft and writes back only when the user leaves the field and the value actually changed.
 * `Escape` reverts to the last committed value. Multiline commits on blur only, so Enter can insert
 * newlines.
 */

type Props = {
  value: string;
  onCommit: (value: string) => void;
  placeholder?: string;
  multiline?: boolean;
  disabled?: boolean;
  /** MUI TextField typography variant driver; 'title' renders larger/bolder for a section heading. */
  variant?: 'title' | 'body';
  fullWidth?: boolean;
};

const InlineEditableText: React.FC<Props> = ({
  value,
  onCommit,
  placeholder,
  multiline = false,
  disabled = false,
  variant = 'body',
  fullWidth = true,
}) => {
  const [draft, setDraft] = useState(value ?? '');
  const committed = useRef(value ?? '');

  // Re-sync when the persisted value changes underneath us (e.g. after a refetch), but not while
  // the user is mid-edit — draft is the source of truth until it commits.
  useEffect(() => {
    committed.current = value ?? '';
    setDraft(value ?? '');
  }, [value]);

  const commit = () => {
    const trimmed = draft;
    if (trimmed !== committed.current) {
      committed.current = trimmed;
      onCommit(trimmed);
    }
  };

  return (
    <TextField
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !multiline) {
          e.preventDefault();
          (e.target as HTMLInputElement).blur();
        }
        if (e.key === 'Escape') {
          setDraft(committed.current);
          (e.target as HTMLInputElement).blur();
        }
      }}
      placeholder={placeholder}
      multiline={multiline}
      minRows={multiline ? 2 : undefined}
      disabled={disabled}
      fullWidth={fullWidth}
      variant="standard"
      InputProps={{
        disableUnderline: false,
        sx:
          variant === 'title'
            ? { fontSize: '1.05rem', fontWeight: 600 }
            : undefined,
      }}
    />
  );
};

export default InlineEditableText;
